import { Injectable } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import * as ExportActions from '@planone/bau365/data-access-export';
import { DataAccessFairsFacade } from '@planone/bau365/data-access-fair-data';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import * as FeatureTrackingSnowplow from '@planone/shared/feature-tracking-snowplow';
import { of, switchMap, withLatestFrom } from 'rxjs';

import { CustomEventIglu } from '../../tracking-types/snowplow/schema/schema.interface';

@Injectable()
export class ExportEffects {
  methodSelectionOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.methodSelectionOpened),
      withLatestFrom(this.fairsFacade.fairId$, this.authenticationFacade.excludeAnalytics$),
      switchMap(([_, fairId, exclude_analytics]) => {
        const data = this.extractData(this.route.snapshot);
        const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'open_export_dialog',
            page_type: pageType,
            context: fairId,
            exclude_analytics: exclude_analytics,
          })
        );
      })
    )
  );

  mailFormOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.mailFormOpened),
      withLatestFrom(this.fairsFacade.fairId$, this.authenticationFacade.excludeAnalytics$),
      switchMap(([_, fairId, exclude_analytics]) => {
        const data = this.extractData(this.route.snapshot);
        const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'open_forward_dialog',
            page_type: pageType,
            context: fairId,
            exclude_analytics: exclude_analytics,
          })
        );
      })
    )
  );

  downloadSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.trackDownloadSucceeded),
      withLatestFrom(this.fairsFacade.fairId$, this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, fairId, exclude_analytics]) => {
        const data = this.extractData(this.route.snapshot);
        const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'export',
            page_type: pageType,
            context: fairId,
            exclude_analytics: exclude_analytics,
            method: 'DOCUMENT_DOWNLOAD',
            file_name: action.file_name,
            file_extension: action.file_extension,
          }),
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.export,
              data: {
                method: 'DOCUMENT_DOWNLOAD',
                context: fairId,
                page_type: pageType,
                exclude_analytics,
                file_name: action.file_name,
                file_extension: action.file_extension,
              },
            },
          })
        );
      })
    )
  );

  forwardSucceeded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.trackForwardSucceeded),
      withLatestFrom(this.fairsFacade.fairId$, this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, fairId, exclude_analytics]) => {
        const data = this.extractData(this.route.snapshot);
        const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);
        let method: string;

        switch (action.action) {
          case 'copy':
            method = 'DOCUMENT_LINK_COPY';
            break;
          case 'email':
            method = 'DOCUMENT_MAIL';
            break;
          default:
            method = 'DOCUMENT_DOWNLOAD';
            break;
        }

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'export',
            page_type: pageType,
            context: fairId,
            exclude_analytics: exclude_analytics,
            method: method,
            file_name: action.file_name,
            file_extension: action.file_extension,
          }),
          FeatureTrackingSnowplow.selfDescribingEvent({
            event: {
              schema: CustomEventIglu.export,
              data: {
                method: method,
                context: fairId,
                page_type: pageType,
                exclude_analytics,
                file_name: action.file_name,
                file_extension: action.file_extension,
              },
            },
          })
        );
      })
    )
  );

  actionFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ExportActions.downloadFailed),
      withLatestFrom(this.fairsFacade.fairId$, this.authenticationFacade.excludeAnalytics$),
      switchMap(([fairId, exclude_analytics]) => {
        const data = this.extractData(this.route.snapshot);
        const pageType = data?.analyticsPageType ?? this.getPageType(this.router.url);

        return of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'export_failed',
            page_type: pageType,
            context: fairId,
            exclude_analytics: exclude_analytics,
            method: 'DOCUMENT_DOWNLOAD',
          })
        );
      })
    )
  );

  constructor(
    private readonly actions$: Actions,
    private router: Router,
    private route: ActivatedRoute,
    private fairsFacade: DataAccessFairsFacade,
    private authenticationFacade: AuthenticationFacade
  ) {}

  extractData(route: SerializedRouterStateSnapshot | ActivatedRouteSnapshot | undefined | null): any {
    let child = route?.root.firstChild;
    let data = {};

    if (child) {
      while (child?.firstChild) {
        child = child.firstChild;
        data = { ...data, ...child.data };
      }
    }

    return data;
  }

  getPageType(url: string) {
    const urlSegments = new URL('http://test.local/' + url).pathname.split('/');
    const pageType = urlSegments[urlSegments.length - 1] ?? '';

    if (pageType.length === 0) {
      return 'home';
    }

    return 'unknown';
  }
}
